/**
 * Text processing utilities for cleaning and formatting message content
 */

/**
 * Strip <system-reminder> tags from text
 */
export function stripSystemReminders(text: string): string {
  return text.replace(/<system-reminder[^>]*>.*?<\/system-reminder>/gs, '').trim();
}

/**
 * Strip <reminder> tags from text
 */
export function stripReminderTags(text: string): string {
  return text
    .replace(/<system-reminder[^>]*>.*?<\/system-reminder>/gs, '')
    .replace(/<reminder[^>]*>.*?<\/reminder>/gs, '')
    .trim();
}

/**
 * Replace compaction request with user-friendly message
 */
export function replaceCompactionRequest(text: string): string {
  return text.replace(
    /<compaction-request[^>]*>.*?<\/compaction-request>/gs,
    '_[Compacted conversation history for context efficiency]_'
  );
}

/**
 * Strip interrupt markers from text
 * Returns null if the text is ONLY an interrupt marker (should hide the block)
 */
export function stripInterruptMarkers(text: string): string | null {
  const stripped = text.replace(/<interrupt-marker[^>]*\/>/g, '').trim();

  // If the text was ONLY an interrupt marker, return null to hide the block
  if (stripped === '' && text.includes('<interrupt-marker')) {
    return null;
  }

  return stripped;
}

/**
 * Parse secret request tags from text
 */
export interface SecretRequestPart {
  type: 'text' | 'secret';
  content: string;
  secretKey?: string;
}

export function parseSecretRequests(text: string): SecretRequestPart[] {
  const regex = /<secret-request\s+key="([^"]+)"\s*\/>/g;
  const parts: SecretRequestPart[] = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }

    // Add the secret request
    parts.push({
      type: 'secret',
      content: match[0],
      secretKey: match[1],
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  // If no secret requests found, return the original text as a single part
  if (parts.length === 0) {
    parts.push({ type: 'text', content: text });
  }

  return parts;
}

/**
 * Format error message based on error type
 */
export function formatErrorMessage(
  content: string,
  errorType?: string
): string {
  switch (errorType) {
    case 'ratelimited':
      return 'AI provider is rate limiting requests';
    case 'overloaded':
      return 'AI provider is currently overloaded. Please try again in a moment';
    case 'internal':
      return `Server error: ${content}`;
    case 'interrupt':
      return 'User interrupted';
    default:
      return content;
  }
}
